import {
  type TableDateFormat,
  type Timezone,
  formatUtcTimestampInTimeZone,
  tableDateFormat,
} from '../../../date';
import { makeTypeGuardByPropertyValue } from '../../../types';
import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import type { TableColumnFormat } from '../../TableEndpoint';
import { createDateTimeFilter } from '../../filters';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import type { TableColumnFormatLink, TableLinkColumnConfig } from '../../links';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';

type DateTimeValue = Date | number | null;

export type TableColumnFormatDateTime<TRow> = {
  type: 'dateTime';
  field: KeyOfType<TRow, DateTimeValue>;
  format: TableDateFormat;
} & TableColumnFormatLink<TRow>;

export const isDateTimeTableColumnFormat = makeTypeGuardByPropertyValue<
  TableColumnFormat<any>,
  TableColumnFormatDateTime<any>
>()('type', 'dateTime');

export const formatDateTimeCellValue = (
  value: DateTimeValue,
  timezone: Timezone | undefined,
  format: TableDateFormat,
): string | null =>
  value
    ? formatUtcTimestampInTimeZone(
        value,
        timezone ?? 'UTC',
        tableDateFormat[format],
      )
    : null;

export const createDateTimeColumn = <TRow>(
  config: TableDataColumnConfig<TRow, DateTimeValue> &
    TableLinkColumnConfig<TRow> & {
      format: TableDateFormat;
      array?: false;
    },
): TableColumnBase<TRow, TableColumnFormatDateTime<TRow>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'dateTime',
    field: config.key,
    format: config.format,
    createLinkProps: config.link?.createLinkProps,
  },
  csvStringDependencies: [config.key],
  webDependencies: [config.key],
  toCsvString: (row) =>
    formatValueToCsvString(
      formatDateTimeCellValue(
        getValueFromKeyOfType<TRow, DateTimeValue>(row, config.key),
        // Export always in UTC and use iso format like it was in old Table.
        // We are aware that this approach is disputable, it will be eventually
        // tuned once new Table component settles down.
        'UTC',
        (
          {
            dateFormat: 'dateFormat',
            dateTimeFormat: 'isoFormat',
            isoFormat: 'isoFormat',
          } as const
        )[config.format],
      ),
      (v: string | null): string => v ?? '',
    ),
  filter: createDefaultFilter(config.filter, createDateTimeFilter()),
});
